/* ==========================================================================
   Company facts — single source of truth.

   ⚠️  FEDERAL REPRESENTATION NOTICE
   Fields under `registration` and `certifications` are read by contracting
   officers and are effectively representations of the business. Every value
   below that is `null` is UNVERIFIED and will NOT render anywhere on the
   site until the client supplies a certificate number or a SAM.gov record.

   To publish a value: replace null with the confirmed value and set the
   matching `verified: true`. Components skip any field that is null.
   ========================================================================== */

export const company = {
  legalName: 'Bethel Innovations LLC',
  shortName: 'Bethel Innovations',
  tagline: 'Securing federal missions through cybersecurity, AI governance and quantum readiness.',
  founded: null, // TODO(client): year established

  contact: {
    phone: '(703) 455-0650',
    phoneHref: 'tel:+17034550650',
    email: 'info@bethelinnovations.com',
    emailHref: 'mailto:info@bethelinnovations.com',
    // TODO(client): confirm mailing address for the capability statement
    address: null,
    hours: 'Monday – Friday, 8:00 a.m. – 6:00 p.m. ET',
  },

  social: [
    { name: 'LinkedIn',  href: 'https://www.linkedin.com/company/bethelinnovations' },
    { name: 'Facebook',  href: 'https://www.facebook.com/people/Bethel-Innovations-LLC/100092667889272/' },
    { name: 'Instagram', href: 'https://www.instagram.com/bethel_innovations/' },
  ],

  /* --------------------------------------------------------------------
     Federal registration data — all pending client verification.
     -------------------------------------------------------------------- */
  registration: {
    uei:        { label: 'UEI',                   value: null, verified: false },
    cage:       { label: 'CAGE Code',             value: null, verified: false },
    samStatus:  { label: 'SAM.gov Registration',  value: null, verified: false },
    sizeStandard: { label: 'Business Size',       value: null, verified: false },
  },

  /* NAICS codes must come from the client's SAM.gov profile — not guessed. */
  naics: [
    // { code: '541512', title: 'Computer Systems Design Services', primary: true },
  ],

  psc: [
    // { code: 'D310', title: 'IT and Telecom — Cyber Security and Data Backup' },
  ],

  /* --------------------------------------------------------------------
     Socio-economic status. The client asked on 19 Aug 2026 to drop the
     minority-owned wording and describe the company as a small business
     only, so that is what publishes. Narrowing a socio-economic claim is
     always safe; widening one is not. Everything else needs a certificate.
     -------------------------------------------------------------------- */
  certifications: [
    {
      name: 'Small Business',
      issuer: 'Self-certified',
      value: null,
      verified: true, // sourced from client-approved copy
    },
    { name: '8(a) Business Development Program', issuer: 'U.S. Small Business Administration', value: null, verified: false },
    { name: 'Small Disadvantaged Business (SDB)', issuer: 'U.S. Small Business Administration', value: null, verified: false },
  ],

  /* Framework alignment is a statement of practice, not a certification —
     it is drawn from the client's own service descriptions. */
  frameworks: [
    'NIST Risk Management Framework (RMF)',
    'NIST SP 800-53',
    'FISMA',
    'FedRAMP',
    'ISO 27001 / 27002',
    'NIST Cybersecurity Framework (CSF)',
    'PCI-DSS',
    'OMB & US-CERT guidance',
  ],

  contractVehicles: [], // TODO(client): vehicles held or pursued
};

/* --------------------------------------------------------------------------
   PREVIEW MODE

   Set REACT_APP_SHOW_PENDING=true to render the not-yet-supplied fields as
   clearly-labelled "awaiting confirmation" rows instead of hiding them. It
   exists so the client can see the structure of the company-data and
   past-performance blocks and understand exactly what each field is for.

   It never invents a value. The rows show a placeholder dash and a "Pending"
   chip — there is nothing here a contracting officer could mistake for a
   representation.

   Off by default, so a production build behaves exactly as before: unverified
   fields simply do not render.
   -------------------------------------------------------------------------- */
export const SHOW_PENDING = process.env.REACT_APP_SHOW_PENDING === 'true';

/** Fields still awaiting client confirmation — only used in preview mode. */
export const pendingRegistration = () =>
  Object.values(company.registration).filter((f) => !(f.verified && f.value));

export const pendingCertifications = () =>
  company.certifications.filter((c) => !c.verified);

/** Registration entries that are safe to display. */
export const publishedRegistration = () =>
  Object.values(company.registration).filter((f) => f.verified && f.value);

/** Certifications that are safe to display. */
export const publishedCertifications = () =>
  company.certifications.filter((c) => c.verified);

/** True when there is enough verified data to render a company-data block. */
export const hasPublishableCompanyData = () =>
  publishedRegistration().length > 0 || company.naics.length > 0;
